import * as React from 'react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';

import { iconVariants, type IconVariantProps } from './icon.variants';
import { cn } from '@/hen-ui/utils';

/**
 * Type for valid Lucide icon names.
 * This is a union of all valid Lucide icon names exported from lucide-react/dynamic.
 * DynamicIcon will handle invalid names gracefully.
 *
 * @example
 * // Valid icon names: 'check', 'x', 'arrow-right', 'heart', 'arrow-up-circle', etc.
 * <Icon icon="check" />
 * <Icon icon="arrow-up-circle" />
 */
export type LucideIconName = IconName;

/**
 * Props for the Icon component.
 * Extends standard SVG element props and supports accessibility attributes.
 *
 * @remarks
 * - `aria-label`: Accessible label for semantic icons. Required if the icon is not decorative.
 * - `aria-hidden`: Whether the icon is decorative. Defaults to false. Set to true for decorative icons.
 */
export interface IconProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'size' | 'color' | 'name'>,
    IconVariantProps {
  /**
   * The name of the Lucide icon to render.
   * Must be a valid Lucide icon name (e.g., 'check', 'x', 'arrow-right').
   * @example
   * <Icon icon="check" />
   * <Icon icon="arrow-right" size="lg" />
   */
  icon: LucideIconName;
}

/**
 * Icon component following HEN Design System standards.
 *
 * A standardized wrapper for Lucide icons that applies HEN Design System tokens
 * for consistent sizing, coloring, and spacing. All styling uses tokens from
 * tailwind.base.hen.config.ts, ensuring themeability and brand consistency.
 *
 * @example
 * // Basic usage with default size and color
 * <Icon icon="check-circle" />
 *
 * @example
 * // With size and color variants
 * <Icon icon="arrow-right" size="lg" color="primary" />
 *
 * @example
 * // As a semantic icon with accessibility
 * <Icon icon="check" aria-label="Item completed" aria-hidden="false" />
 *
 * @example
 * // As a decorative icon (default)
 * <Icon icon="clipboard" aria-hidden="true" />
 *
 * @example
 * // With custom className using design tokens
 * <Icon icon="arrow-down" className="text-primary-700" />
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      icon,
      size,
      color,
      background,
      className,
      'aria-label': ariaLabel,
      'aria-hidden': ariaHidden = false,
      ...props
    },
    ref,
  ) => {
    // Only include aria-label if provided and icon is not hidden
    // (aria-hidden={true} means the icon is decorative and shouldn't have a label)
    const ariaAttributes = {
      'aria-hidden': ariaHidden,
      ...(ariaLabel && !ariaHidden ? { 'aria-label': ariaLabel } : {}),
    };

    return (
      <DynamicIcon
        name={icon}
        ref={ref}
        className={cn(iconVariants({ size, color, background }), className)}
        {...ariaAttributes}
        {...props}
      />
    );
  },
);

Icon.displayName = 'Icon';
